import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { forkJoin, map, Observable } from 'rxjs';
import { HTTP_LOADER_CONFIG } from './http-loader-config';

class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private readonly http: HttpClient,
    private readonly resources: Array<{ prefix: string; suffix: string }>,
  ) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin(
      this.resources.map((resource) =>
        this.http.get<TranslationObject>(
          `${resource.prefix}${lang}${resource.suffix}`,
        ),
      ),
    ).pipe(map((translations) => deepMergeTranslations(...translations)));
  }
}

/**
 * Factory function for creating TranslateHttpLoader with configurable paths
 * @param http - HttpClient instance
 * @returns TranslateHttpLoader instance
 */
export const httpLoaderFactory: (http: HttpClient) => TranslateLoader = (
  http: HttpClient,
) => {
  const config = inject(HTTP_LOADER_CONFIG);
  if (config.resources?.length) {
    return new MultiTranslateHttpLoader(http, config.resources);
  }
  return new TranslateHttpLoader(http, config.prefix, config.suffix);
};

function deepMergeTranslations(
  ...translations: TranslationObject[]
): TranslationObject {
  return translations.reduce<TranslationObject>(
    (merged, translation) => deepMerge(merged, translation),
    {},
  );
}

function deepMerge(
  target: TranslationObject,
  source: TranslationObject,
): TranslationObject {
  const result: TranslationObject = { ...target };

  for (const [key, value] of Object.entries(source)) {
    const current = result[key];
    result[key] =
      isPlainObject(current) && isPlainObject(value)
        ? deepMerge(current, value)
        : value;
  }

  return result;
}

function isPlainObject(value: unknown): value is TranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
